<template>
  <v-dialog v-model="dialog" persistent max-width="720px">
    <v-card class="lead-dialog-card">
      <div class="dialog-header">
        <div>
          <div class="dialog-title">Edit Lead</div>
          <div class="dialog-subtitle">
            Update lead details and follow-up status.
          </div>
        </div>

        <v-btn icon small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="pa-4">
        <v-row dense>
          <v-col cols="12" md="7">
            <div class="field-label">Business Name</div>
            <v-text-field
              outlined
              dense
              :hide-details="nameErrors.length === 0"
              :error-messages="nameErrors"
              v-model="Lead.name"
              label="Business Name"
              @change="Lead.name = properCase(Lead.name)"
            />
          </v-col>

          <v-col cols="12" md="5">
            <div class="field-label">Status</div>
            <v-select
              dense
              outlined
              :items="leadStatus"
              v-model="Lead.status"
              label="Status"
              hide-details
            />
          </v-col>

          <v-col cols="12">
            <div class="field-label">Contact Name</div>
            <v-text-field
              outlined
              dense
              hide-details
              v-model="Lead.contactName"
              label="Contact Name"
              @change="Lead.contactName = properCase(Lead.contactName)"
            />
          </v-col>

          <v-col cols="12" md="6">
            <div class="field-label">Phone Number</div>
            <v-text-field
              outlined
              dense
              hide-details
              v-model="Lead.phoneNumber"
              label="Phone Number"
              prepend-inner-icon="mdi-phone-outline"
            />
          </v-col>

          <v-col cols="12" md="6">
            <div class="field-label">Email Address</div>
            <v-text-field
              outlined
              dense
              hide-details
              v-model="Lead.email"
              label="Email Address"
              prepend-inner-icon="mdi-email-outline"
            />
          </v-col>

          <v-col cols="12">
            <div class="field-label">Address</div>
            <v-text-field
              outlined
              dense
              hide-details
              v-model="Lead.address"
              label="Address"
              prepend-inner-icon="mdi-map-marker-outline"
            />
          </v-col>

          <v-col cols="12" md="4">
            <div class="field-label">City</div>
            <v-text-field
              outlined
              dense
              hide-details
              v-model="Lead.city"
              label="City"
              @change="Lead.city = properCase(Lead.city)"
            />
          </v-col>

          <v-col cols="12" md="4">
            <div class="field-label">State</div>
            <v-text-field
              outlined
              dense
              hide-details
              v-model="Lead.state"
              label="State"
            />
          </v-col>

          <v-col cols="12" md="4">
            <div class="field-label">Zip Code</div>
            <v-text-field
              outlined
              dense
              hide-details
              v-model="Lead.zip"
              label="Zip Code"
            />
          </v-col>

          <v-col cols="12">
            <div class="field-label">Note</div>
            <v-textarea
              outlined
              dense
              hide-details
              auto-grow
              rows="3"
              v-model="Lead.note"
              :label="$t('labels.note')"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-actions class="dialog-actions">
        <v-spacer />
        <v-btn text color="primary" @click="close"> Close </v-btn>
        <v-btn
          color="primary"
          depressed
          rounded
          dark
          :loading="loading"
          @click="submit"
        >
          Update Lead
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import LeadService from "../service.js";
import GlobalFunction from "@/mixins/GlobalFunction";
import _ from "lodash";

const { validationMixin } = require("vuelidate");
const { required } = require("vuelidate/lib/validators");

export default {
  name: "update-lead",

  props: {
    editDialog: {
      default: false,
    },
    editLead: {
      type: Object,
    },
  },

  data() {
    return {
      leadStatus: [
        "Hot Lead",
        "Warm Lead",
        "Cold",
        "Not Ordered",
        "Closed",
        "Convert",
        "Dead",
      ],
      loading: false,
      dialog: false,
      Lead: this.emptyLead(),
    };
  },

  mixins: [validationMixin, GlobalFunction],

  validations: {
    Lead: {
      name: {
        required,
      },
    },
  },

  computed: {
    ...mapGetters("global", ["currentUser"]),

    nameErrors() {
      const errors = [];
      if (!this.$v.Lead.name.$dirty) return errors;
      if (!this.$v.Lead.name.required) {
        errors.push(this.$t("validations.fieldIsRequired"));
      }
      return errors;
    },
  },

  watch: {
    editDialog(val) {
      this.Lead = _.clone(this.editLead || this.emptyLead());
      this.dialog = val;
    },
  },

  methods: {
    emptyLead() {
      return {
        name: null,
        email: null,
        phoneNumber: null,
        contactName: null,
        address: null,
        city: null,
        state: null,
        zip: null,
        note: null,
        status: "Hot Lead",
      };
    },

    close() {
      this.empty();

      this.dialog = false;

      this.$emit("close");
    },

    empty() {
      this.Lead = this.emptyLead();
      this.$v.$reset();
    },

    submit() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.$swal

          .mixin({
            toast: true,

            position: "top-end",

            showConfirmButton: false,

            timer: 3000,
          })

          .fire({
            icon: "error",

            title: this.$t("toastMessage.text.invalidFields"),
          });

        return;
      }

      this.loading = true;

      return LeadService.update(this.Lead.id, this.Lead)

        .then((result) => {
          if (result.status) {
            this.$swal

              .mixin({
                toast: true,

                position: "top-end",

                showConfirmButton: false,

                timer: 3000,
              })

              .fire({
                icon: "success",

                title: "Lead is updated",
              });

            this.$emit("submit");

            this.close();
          }
        })

        .catch((err) => {
          this.$swal

            .mixin({
              toast: true,

              position: "top-end",

              showConfirmButton: false,

              timer: 3000,
            })

            .fire({
              icon: "error",

              title: err.data ? err.data.message : "Unable to update lead",
            });
        })

        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
<style scoped>
.lead-dialog-card {
  border-radius: 20px;
  overflow: hidden;
}

.dialog-header {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.dialog-title {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
}

.dialog-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.field-label {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 6px;
}

.dialog-actions {
  padding: 12px 16px;
}
</style>